import {
  Card,
  CardContent,
  Grid,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material'
import { GreenText, OrderableTableCell } from 'components'
import { VehicleInvoicedDto } from 'dtos/invoicing'
import { useEffect, useState } from 'react'
import { clientSideOrderBy, clientSidePagination, formatDateMoment } from 'utils'
import InvoicingDocumentsDownloadLink from './InvoicingDocumentsDownloadLink'

interface InvoicedCardProps {
  invoiced: VehicleInvoicedDto[]
}

export default function InvoicedCard({ invoiced }: InvoicedCardProps) {
  const [order, setOrder] = useState<'asc' | 'desc'>('asc')
  const [orderBy, setOrderBy] = useState<keyof VehicleInvoicedDto>('importerStockNumber')
  const [page, setPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(10)

  const onChangeSortByDirection = (propertyName: keyof VehicleInvoicedDto) => {
    setOrder(orderBy === propertyName && order === 'asc' ? 'desc' : 'asc')
    setOrderBy(propertyName)
  }

  useEffect(() => {
    setPage(0)
  }, [pageSize])

  return (
    <Card>
      <CardContent>
        <Grid container spacing={2} alignItems='center'>
          <Grid item xs={12} sm='auto'>
            <Typography fontWeight='bold' variant='h6'>
              Invoiced
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TableContainer>
              <Table>
                <TableHead>
                  <OrderableTableCell<VehicleInvoicedDto> keyName='importerStockNumber' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                    Importer Stock #
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicedDto>
                    keyName='buyerExporterCompany'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                  >
                    Buyer/Exporter
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicedDto> keyName='papsNumber' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                    PAPS #
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicedDto> keyName='vin' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                    VIN
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicedDto> keyName='year' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                    Year
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicedDto> keyName='make' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                    Make
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicedDto> keyName='model' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                    Model
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicedDto>
                    disabled
                    keyName='vehicleGuid'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                  >
                    Invoicing Documents
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicedDto>
                    keyName='readyToInvoiceDateTime'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                  >
                    Ready To Invoice
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicedDto>
                    keyName='sentToQuickBooksDateTime'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                  >
                    Sent To QuickBooks
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicedDto>
                    keyName='quickBooksInvoiceNumber'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                  >
                    QB Invoice #
                  </OrderableTableCell>
                </TableHead>

                <TableBody>
                  {invoiced
                    ?.sort(clientSideOrderBy<VehicleInvoicedDto>(orderBy, order))
                    .slice(...clientSidePagination(page, pageSize))
                    .map((vehicle, index) => (
                      <TableRow key={vehicle.vehicleGuid} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                        <TableCell>{vehicle.importerStockNumber}</TableCell>

                        <TableCell>{vehicle.buyerExporterCompany}</TableCell>

                        <TableCell>{vehicle.papsNumber}</TableCell>

                        <TableCell>{vehicle.vin}</TableCell>

                        <TableCell>{vehicle.year}</TableCell>

                        <TableCell>{vehicle.make}</TableCell>

                        <TableCell>{vehicle.model}</TableCell>

                        <TableCell>
                          <InvoicingDocumentsDownloadLink vehicleGuid={vehicle.vehicleGuid} />
                        </TableCell>

                        <TableCell>{vehicle.readyToInvoiceDateTime ? formatDateMoment(vehicle.readyToInvoiceDateTime) : '-'}</TableCell>

                        <TableCell>{vehicle.readyToInvoiceDateTime ? formatDateMoment(vehicle.sentToQuickBooksDateTime) : '-'}</TableCell>

                        <TableCell>{vehicle.quickBooksInvoiceNumber}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>

                <TableFooter>
                  <TableRow>
                    <TablePagination
                      count={invoiced.length ?? 0}
                      onPageChange={(_, page) => setPage(page)}
                      onRowsPerPageChange={e => setPageSize(e.target.value as unknown as number)}
                      page={page}
                      rowsPerPage={pageSize}
                      rowsPerPageOptions={[5, 10, 25, 50, 100]}
                      SelectProps={{ fullWidth: false }}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Grid>

          <Grid item xs={12}>
            <GreenText variant='outlined'>
              {`**Invoiced:**

This list displays vehicles that have been fully processed and invoiced.`}
            </GreenText>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
